import { useEffect, useState } from 'react';
import { Button } from '@/components/shadcn/button';
import { Input } from '@/components/shadcn/input';
import { Label } from '@/components/shadcn/label';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/shadcn/sheet';
import { pointPickerValues } from './pickleballScoringUtils';

interface PickleballPointPickerSheetProps {
  open: boolean;
  side: 'a' | 'b' | null;
  currentValue: number;
  scoringType: string;
  onClose: () => void;
  onSelect: (value: number) => void;
}

export default function PickleballPointPickerSheet({
  open,
  side,
  currentValue,
  scoringType,
  onClose,
  onSelect,
}: PickleballPointPickerSheetProps) {
  const presets = pointPickerValues(scoringType);
  const inGrid = presets.includes(currentValue);
  const [showCustom, setShowCustom] = useState(!inGrid);
  const [customValue, setCustomValue] = useState(inGrid ? '0' : String(currentValue));

  useEffect(() => {
    if (!open) return;
    const listed = pointPickerValues(scoringType).includes(currentValue);
    setShowCustom(!listed);
    setCustomValue(listed ? '0' : String(currentValue));
  }, [open, currentValue, scoringType]);

  return (
    <Sheet open={open} onOpenChange={(next) => !next && onClose()}>
      <SheetContent side="bottom" showCloseButton={false} className="rounded-t-3xl px-5 pb-6 pt-3">
        <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-gray-200" />
        <SheetHeader className="space-y-1 text-left">
          <SheetTitle className="text-lg font-extrabold text-[#0E1726]">Pilih Poin Akhir</SheetTitle>
          <SheetDescription className="text-sm text-gray-400">
            {side === 'b' ? 'Sisi kanan' : 'Sisi kiri'} · poin akhir set ini
          </SheetDescription>
        </SheetHeader>
        <div className="mt-4 grid grid-cols-4 gap-2">
          {presets.map((value) => {
            const selected = !showCustom && currentValue === value;
            return (
              <Button
                key={value}
                type="button"
                variant="outline"
                onClick={() => onSelect(value)}
                className={`h-14 rounded-2xl text-base font-extrabold ${
                  selected ? 'border-[#FC480D] bg-white text-[#FC480D]' : 'border-gray-200 text-[#0E1726]'
                }`}
              >
                {value}
              </Button>
            );
          })}
        </div>
        <Button
          type="button"
          variant="ghost"
          className="mt-3 w-full text-sm font-semibold text-[#FC480D]"
          onClick={() => setShowCustom(true)}
        >
          Poin lain
        </Button>
        {showCustom && (
          <div className="mt-2 space-y-2">
            <Label htmlFor="pickleball-custom-score">Poin custom</Label>
            <Input
              id="pickleball-custom-score"
              inputMode="numeric"
              value={customValue}
              onChange={(e) => setCustomValue(e.target.value)}
            />
            <Button
              type="button"
              className="h-11 w-full rounded-full bg-[#FC480D] font-bold text-white hover:bg-[#D83B08]"
              onClick={() => onSelect(Number.parseInt(customValue, 10) || 0)}
            >
              Pakai poin ini
            </Button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
